/**
 * Bookmarks — operator-authored marks on one camera's archive span (VMS-665).
 * A camera is its deviceId. The span is half-open UTC wall-clock time [startTimeMs,
 * startTimeMs + durationMs).
 */

import { z } from 'zod';

import type { CaseDetail } from './cases';
import {
    Bookmark as BookmarkSchema,
    BookmarkTag as BookmarkTagSchema,
    UpsertBookmarksResponse as GeneratedBookmarksAcceptedSchema,
} from './generated/vms_database.zod';
import type { LoadState } from './snapshot';

export { BookmarkSchema };

/** A stored bookmark, or a create's own draft, which has no times yet. */
export type Bookmark = Omit<z.infer<typeof BookmarkSchema>, 'createdAt' | 'updatedAt'> &
    Partial<Pick<z.infer<typeof BookmarkSchema>, 'createdAt' | 'updatedAt'>>;

/** What a create of one bookmark answers: the batch of one, stored whole. */
export const BookmarksAcceptedSchema = GeneratedBookmarksAcceptedSchema.extend({
    accepted: z.literal(1),
});

/** What a create sends: the cloud mints a missing id. */
export type BookmarkDraft =
    Omit<Bookmark, 'id' | 'createdAt' | 'updatedAt'> & { id?: string };

/**
 * Conjunctive list filters: fromMs/toMs admit spans overlapping
 * [fromMs, toMs), every given tag must be carried, and text is a substring
 * of the description in any letter case. limit caps the rows, newest first;
 * the server clamps it to its own maximum.
 */
export interface BookmarkListFilter {
    deviceId?: string;
    fromMs?: number;
    toMs?: number;
    tags?: string[];
    text?: string;
    limit?: number;
}

export interface BookmarkListSnapshot extends LoadState {
    bookmarks: Bookmark[];
    /** True from the first load that succeeded, also with no rows. */
    isLoaded: boolean;
}

/** The longest span one protected bookmark may pin: one day. */
export const MAX_PROTECTED_BOOKMARK_DURATION_MS = 24 * 60 * 60 * 1000;

/** What a protected bookmark's shield says. */
export const PROTECTED_BOOKMARK_TITLE =
    'The archive under this bookmark is protected from cleanup';

/**
 * One name of the organization's tag vocabulary, with how many bookmarks
 * carry it. The name is the identity; the vocabulary is a cloud-side
 * aggregate of the bookmarks themselves, so a tag nothing carries does not
 * exist.
 */
export { BookmarkTagSchema };
export type BookmarkTag = z.infer<typeof BookmarkTagSchema>;

export interface BookmarkTagsSnapshot extends LoadState {
    /** Most used first: the server sorts by count, then by name. */
    tags: BookmarkTag[];
}

/** What a bookmark write tells the families; each mounted member reads again. */
export enum BookmarkChange {
    Written = 'written',
}

/** What a bookmark card offers; the panel hosting it makes the writes. */
export interface BookmarkCardActions {
    /** Administrators only: protection changes what a server's cleaner may delete. */
    canProtect: boolean;
    setProtected: (bookmark: Bookmark, shouldProtect: boolean) => Promise<void>;
    /** Settles true once the bookmark is gone. */
    deleteBookmark: (bookmark: Bookmark) => Promise<boolean>;
    /** Files the bookmark into the case, or into a new one when handed null; says how it went. */
    fileInto: (bookmark: Bookmark, record: CaseDetail | null) => Promise<void>;
}

/** What the bookmark form says about a bookmark; the span and the camera are its host's. */
export interface BookmarkFields {
    description: string;
    tags: string[];
    protectArchive: boolean;
}

/** The tabs of the Bookmarks panel. */
export enum BookmarksTab {
    Bookmarks = 'bookmarks',
    Cases = 'cases',
}
